"use client";

import React from "react";
import {
  Combobox,
  HStack,
  Portal,
  TagsInput,
  Text,
  useCombobox,
  useFilter,
  useListCollection,
  useTagsInput,
} from "@chakra-ui/react";
import { useColorModeValue } from "components/ui/color-mode";
import AppAvatar from "components/utils/AppAvatar";
import { getAvatarHexColor, getAvatarTint } from "utils/avatar";

function defaultColorForTag(seed, isDark = false) {
  return getAvatarTint(seed, 12);
}

export function TagCombobox({
  label = "Tags",
  placeholder = "Add tag...",
  items = [],
  defaultValue = [],
  value: valueProp,
  onChange,
  allowCustomValue = false,
  onCreateItem,
  createLabel = "Crear",
  name,
  showAvatar = false,
  colorForTag = defaultColorForTag,
}) {
  const isDark = useColorModeValue(false, true);
  const uid = React.useId();
  const isControlled = Array.isArray(valueProp);
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [inputValue, setInputValue] = React.useState("");
  const [createdItems, setCreatedItems] = React.useState([]);
  const value = isControlled ? valueProp : internalValue;
  const allItems = React.useMemo(() => {
    const seen = new Set();
    return [...items, ...createdItems].filter((item) => {
      if (!item?.value || seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });
  }, [items, createdItems]);

  const setValue = React.useCallback(
    (next) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  const itemById = React.useMemo(() => {
    const map = new Map();
    for (const item of allItems) {
      map.set(item.value, item);
    }
    return map;
  }, [allItems]);

  const { contains } = useFilter({ sensitivity: "base" });

  const { collection, filter } = useListCollection({
    initialItems: allItems,
    itemToString: (item) => item?.label ?? "",
    itemToValue: (item) => item?.value ?? "",
    filter: (itemText, query) => contains(itemText ?? "", query ?? ""),
  });

  const ids = React.useMemo(
    () => ({ input: `tag_input_${uid}`, control: `tag_control_${uid}` }),
    [uid]
  );

  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    collection.setItems(allItems);
  }, [allItems, collection]);

  const tags = useTagsInput({
    ids,
    value,
    onValueChange: (event) => setValue(event.value),
    onInputValueChange: (event) => {
      const next = event.inputValue ?? "";
      setInputValue(next);
      filter(next);
      setOpen(true);
    },
  });

  const createFromInput = React.useCallback(async () => {
    const labelText = inputValue.trim();
    if (!labelText || !onCreateItem) return false;

    const existing = allItems.find(
      (item) => String(item.label || "").trim().toLocaleLowerCase("es-MX") === labelText.toLocaleLowerCase("es-MX")
    );
    if (existing) {
      if (!tags.value.includes(existing.value)) tags.addValue(existing.value);
      setInputValue("");
      filter("");
      setOpen(false);
      return true;
    }

    const created = await onCreateItem(labelText);
    const item = created && typeof created === "object"
      ? { label: created.label ?? created.nombre ?? labelText, value: created.value ?? created.id }
      : null;
    if (!item?.value) return false;

    setCreatedItems((prev) => [...prev, item]);
    if (!tags.value.includes(item.value)) tags.addValue(item.value);
    setInputValue("");
    filter("");
    setOpen(false);
    return true;
  }, [allItems, filter, inputValue, onCreateItem, tags]);

  const combobox = useCombobox({
    ids,
    collection,
    open,
    positioning: { strategy: "fixed", hideWhenDetached: true },
    onOpenChange: (event) => setOpen(event.open),
    value: [],
    allowCustomValue,
    openOnClick: true,
    openOnChange: () => true,
    selectionBehavior: "clear",
    onInputValueChange(event) {
      const next = event.inputValue ?? "";
      setInputValue(next);
      filter(next);
    },
    onValueChange(event) {
      const nextId = event.value?.[0];
      if (!nextId) return;
      if (tags.value.includes(nextId)) return;
      tags.addValue(nextId);
    },
  });

  const availableItems = collection.items.filter((item) => !tags.value.includes(item.value));
  const canCreate = !!onCreateItem && inputValue.trim().length > 0;

  const handleEnterSelectFirst = React.useCallback(
    async (event) => {
      if (event.key !== "Enter") return;
      const first = availableItems[0];
      event.preventDefault();
      event.stopPropagation();
      if (!first) {
        await createFromInput();
        return;
      }
      combobox.selectValue(first.value);
    },
    [availableItems, combobox, createFromInput]
  );

  return (
    <Combobox.RootProvider value={combobox}>
      <TagsInput.RootProvider value={tags}>
        <TagsInput.Label fontWeight="bold">{label}</TagsInput.Label>

        <TagsInput.Control
          minH="11"
          borderWidth="2px"
          borderColor="dashboard.border.default"
          bg="dashboard.bg.input"
          color="dashboard.fg.default"
          rounded="2xl"
          px="3"
          py="2"
          gap="1.5"
          flexWrap="wrap"
          transition="all 160ms ease"
          _hover={{ borderColor: "dashboard.accent.border", bg: "dashboard.bg.surfaceMuted" }}
          _focusWithin={{
            borderColor: "dashboard.accent.border",
            boxShadow: "0 0 0 3px rgba(229,57,53,0.15)",
            bg: "dashboard.bg.input",
          }}
        >
          {tags.value.map((id, index) => {
            const tagItem = itemById.get(id);
            const text = tagItem?.label ?? id;
            const avatar = tagItem?.avatar;
            const colorSeed = tagItem?.value || id || text;
            const accentColor = getAvatarHexColor(colorSeed);

            return (
              <TagsInput.Item key={`${id}-${index}`} index={index} value={id}>
                <TagsInput.ItemPreview
                  style={{ backgroundColor: colorForTag(colorSeed, isDark) }}
                  color="dashboard.fg.default"
                  borderWidth="2px"
                  borderColor={accentColor}
                  px="3"
                  py="1"
                  rounded="pill"
                  fontSize="sm"
                  fontWeight="700"
                >
                  <HStack gap="1.5">
                    {showAvatar ? (
                      <AppAvatar size="2xs" name={text} src={avatar} seed={id || text} />
                    ) : null}
                    <TagsInput.ItemText>{text}</TagsInput.ItemText>
                  </HStack>
                  <TagsInput.ItemDeleteTrigger />
                </TagsInput.ItemPreview>
              </TagsInput.Item>
            );
          })}

          <Combobox.Input unstyled asChild>
            <TagsInput.Input
              placeholder={placeholder}
              fontWeight="600"
              color="dashboard.fg.default"
              _placeholder={{ color: "dashboard.fg.subtle" }}
              onKeyDownCapture={handleEnterSelectFirst}
            />
          </Combobox.Input>

          <TagsInput.ClearTrigger />
        </TagsInput.Control>

        <Portal>
          <Combobox.Positioner zIndex="modal">
            <Combobox.Content
              bg="dashboard.bg.popover"
              color="dashboard.fg.default"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
              rounded="xl"
              boxShadow="0 20px 38px rgba(183,28,28,0.10)"
              maxH="240px"
              overflowY="auto"
              p="1.5"
            >
              <Combobox.Empty px="2" py="2">
                {canCreate ? (
                  <Text
                    as="button"
                    type="button"
                    fontWeight="700"
                    color="dashboard.accent.fg"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={createFromInput}
                  >
                    {createLabel} "{inputValue.trim()}"
                  </Text>
                ) : (
                  "No hay resultados"
                )}
              </Combobox.Empty>
              {availableItems.map((item) => (
                <Combobox.Item
                  item={item}
                  key={item.value}
                  px="3"
                  py="2"
                  rounded="lg"
                  fontWeight="600"
                  _highlighted={{ bg: "dashboard.accent.subtle", color: "dashboard.accent.fg" }}
                >
                  <HStack gap="2">
                    {showAvatar ? (
                      <AppAvatar size="2xs" name={item.label} src={item.avatar} seed={item.value || item.label} />
                    ) : null}
                    <Text>{item.label}</Text>
                  </HStack>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>

        <TagsInput.HiddenInput name={name} />
      </TagsInput.RootProvider>
    </Combobox.RootProvider>
  );
}

export default TagCombobox;
